import { useSuspenseQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { type OpenAPIV3, shouldIgnoreEntity } from '@gitbook/openapi-parser';
import { IntlPluralize } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Card,
    Description,
    EmptyState,
    FieldGroup,
    Heading,
    LoadingPanel,
    Tab,
    Tabs,
} from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiQuery } from '../../../../state';
import { OpenAPISelectableItems } from './OpenAPISelectableItems';
import { OpenAPISelectionList } from './OpenAPISelectionList';
import type {
    InsertOpenAPIBlockDialogFormInputs,
    OperationItem,
    SchemaItem,
    WebhookItem,
} from './types';

type OpenAPIBlockSelectionTab = 'operations' | 'schemas' | 'webhooks';

export const HTTP_METHODS = [
    'get',
    'post',
    'put',
    'delete',
    'patch',
    'options',
    'head',
    'connect',
    'trace',
] as const;

/**
 * OpenAPI blocks selection wrapper
 */
export function OpenAPIBlockSelection(props: {
    organizationId: string;
    specSlug: string;
    form: UseFormReturn<InsertOpenAPIBlockDialogFormInputs>;
}) {
    const { organizationId, specSlug, form } = props;

    const operations = form.watch('operations');
    const schemas = form.watch('schemas');
    const webhooks = form.watch('webhooks');

    const [state, setState] = useState<{
        data: OpenAPIV3.Document | null;
        error: Error | null;
    }>({ data: null, error: null });

    const [tab, setTab] = useState<OpenAPIBlockSelectionTab>('operations');

    const { data: latestVersionContent } = useSuspenseQuery(
        apiQuery('getLatestOpenAPISpecVersionContent', {
            organizationId,
            specSlug,
        })
    );

    React.useEffect(() => {
        const parse = async () => {
            const { dereference } = await import('@gitbook/openapi-parser');
            const result = await dereference(latestVersionContent.filesystem);
            if (!result.schema) {
                throw new Error('Failed to parse OpenAPI spec');
            }
            return result.schema as OpenAPIV3.Document;
        };
        parse()
            .then((value) => {
                setState({ data: value, error: null });
            })
            .catch((error) => {
                setState({ data: null, error });
            });
    }, [latestVersionContent]);

    const spec = state.data;

    const items = useMemo((): {
        operations: OperationItem[];
        schemas: SchemaItem[];
        webhooks: WebhookItem[];
    } => {
        if (!spec) {
            return { operations: [], schemas: [], webhooks: [] };
        }
        const operations = Object.entries(spec.paths ?? {}).flatMap(([path, operations]) => {
            if (!operations) {
                return [];
            }
            return HTTP_METHODS.map((method) => {
                const operation = operations[method];
                if (!operation || shouldIgnoreEntity(operation)) {
                    return null;
                }
                return {
                    value: formatOperation({ method, path }),
                    method,
                    path,
                    spec: operation,
                };
            }).filter(filterOutNullable);
        });

        const schemas = Object.entries(spec.components?.schemas ?? {}).flatMap(([name, schema]) => {
            if (!schema || shouldIgnoreEntity(schema)) {
                return [];
            }
            return {
                value: name,
                spec: schema,
            };
        });

        const webhooks = Object.entries(spec.webhooks ?? {}).flatMap(([name, webhook]) => {
            if (!webhook || shouldIgnoreEntity(webhook)) {
                return [];
            }
            return Object.entries(webhook).flatMap(([method, operation]) => {
                if (!operation || shouldIgnoreEntity(operation)) {
                    return [];
                }
                return {
                    value: formatOperation({ method, path: name }),
                    method,
                    name,
                    spec: operation,
                };
            });
        });

        return {
            operations,
            schemas,
            webhooks,
        };
    }, [spec]);

    const selectedItems = useMemo(() => {
        return {
            operations: operations
                .map((value) => items.operations.find((item) => item.value === value))
                .filter(Boolean) as typeof items.operations,
            schemas: schemas
                .map((value) => items.schemas.find((item) => item.value === value))
                .filter(Boolean) as typeof items.schemas,
            webhooks: webhooks
                .map((value) => items.webhooks.find((item) => item.value === value))
                .filter(Boolean) as typeof items.webhooks,
        };
    }, [items, operations, schemas, webhooks]);

    if (!spec) {
        return <LoadingPanel />;
    }

    if (state.error) {
        throw state.error;
    }

    if (!items.operations.length && !items.schemas.length && !items.webhooks.length) {
        return (
            <EmptyState>
                <Heading>No operations, schemas or webhooks found in OpenAPI specification</Heading>
            </EmptyState>
        );
    }

    const handleToggleOperations = (value: string) => {
        if (operations.includes(value)) {
            form.setValue(
                'operations',
                operations.filter((v) => v !== value)
            );
        } else {
            form.setValue('operations', [...operations, value]);
        }
    };

    const handleToggleSchemas = (value: string) => {
        if (schemas.includes(value)) {
            form.setValue(
                'schemas',
                schemas.filter((v) => v !== value)
            );
        } else {
            form.setValue('schemas', [...schemas, value]);
        }
    };

    const handleToggleWebhooks = (value: string) => {
        if (webhooks.includes(value)) {
            form.setValue(
                'webhooks',
                webhooks.filter((v) => v !== value)
            );
        } else {
            form.setValue('webhooks', [...webhooks, value]);
        }
    };

    const specTitle = spec.info?.title || specSlug;

    return (
        <div className="flex flex-1 gap-1">
            <div className="flex flex-1 flex-col">
                <Tabs className="mb-4 border-b">
                    <Tab
                        active={tab === 'operations'}
                        label="Operations"
                        trailingIcon={
                            <Badge
                                className="px-1 text-ui-small"
                                kind="secondary"
                                label={items.operations.length}
                            />
                        }
                        onPress={() => setTab('operations')}
                    />

                    <Tab
                        active={tab === 'webhooks'}
                        trailingIcon={
                            <Badge
                                className="px-1 text-ui-small"
                                kind="secondary"
                                label={items.webhooks.length}
                            />
                        }
                        label="Webhooks"
                        onPress={() => setTab('webhooks')}
                    />

                    <Tab
                        active={tab === 'schemas'}
                        trailingIcon={
                            <Badge
                                className="px-1 text-ui-small"
                                kind="secondary"
                                label={items.schemas.length}
                            />
                        }
                        label="Schemas"
                        onPress={() => setTab('schemas')}
                    />
                </Tabs>

                {tab === 'operations' && (
                    <OpenAPISelectableItems
                        type="operation"
                        specTitle={specTitle}
                        items={items.operations}
                        onToggle={handleToggleOperations}
                        renderCardContent={(props) => <OperationCardContent {...props} />}
                        searchKeys={[
                            { name: 'method', weight: 3 },
                            { name: 'path', weight: 2 },
                            { name: 'spec.summary', weight: 1 },
                            { name: 'spec.operationId', weight: 2 },
                        ]}
                    />
                )}
                {tab === 'schemas' && (
                    <OpenAPISelectableItems
                        type="schema"
                        specTitle={specTitle}
                        items={items.schemas}
                        onToggle={handleToggleSchemas}
                        renderCardContent={(props) => <SchemaCardContent {...props} />}
                        searchKeys={[
                            { name: 'value', weight: 3 },
                            { name: 'spec.description', weight: 2 },
                        ]}
                    />
                )}
                {tab === 'webhooks' && (
                    <OpenAPISelectableItems
                        type="webhook"
                        specTitle={specTitle}
                        items={items.webhooks}
                        onToggle={handleToggleWebhooks}
                        renderCardContent={(props) => <WebhookCardContent {...props} />}
                        searchKeys={[
                            { name: 'method', weight: 3 },
                            { name: 'name', weight: 2 },
                            { name: 'spec.summary', weight: 1 },
                            { name: 'spec.operationId', weight: 2 },
                        ]}
                    />
                )}
            </div>
            <div className="flex items-center p-4">
                <Icon.ChevronRight className="size-6 text-muted" />
            </div>
            <FieldGroup className="flex-1">
                <div className="flex h-full flex-col gap-5">
                    <SelectedItems
                        type="operation"
                        selectedItems={selectedItems.operations}
                        onToggle={handleToggleOperations}
                    />
                    <SelectedItems
                        type="webhook"
                        selectedItems={selectedItems.webhooks}
                        onToggle={handleToggleWebhooks}
                    />
                    <SelectedItems
                        type="schema"
                        selectedItems={selectedItems.schemas}
                        onToggle={handleToggleSchemas}
                    />
                </div>
            </FieldGroup>
        </div>
    );
}

function SelectedItems(props: {
    type: 'operation' | 'schema' | 'webhook';
    selectedItems: (OperationItem | SchemaItem)[];
    onToggle: (value: string) => void;
}) {
    const { selectedItems, onToggle, type } = props;

    return (
        <div className="flex flex-1 flex-col gap-2">
            <Heading>
                <IntlPluralize
                    count={selectedItems.length}
                    singular={`1 ${type} selected`}
                    plural={`${selectedItems.length} ${type}s selected`}
                    none={`No ${type}s selected`}
                />
            </Heading>
            <div className="flex flex-1 flex-col rounded border px-3">
                {selectedItems.length > 0 ? (
                    <OpenAPISelectionList className="-mx-3 p-3" items={selectedItems}>
                        {({ item, index, ref }) => {
                            return (
                                <Card key={index} ref={ref} data-index={index}>
                                    <CardContent item={item}>
                                        <Button
                                            iconOnly
                                            kind="blank"
                                            label="Remove"
                                            leadingIcon={Icon.X}
                                            onPress={() => onToggle(item.value)}
                                        />
                                    </CardContent>
                                </Card>
                            );
                        }}
                    </OpenAPISelectionList>
                ) : (
                    <EmptyState>
                        <Heading>No {type} selected</Heading>
                        <Description>
                            Select a {type} from the list to add it to the document.
                        </Description>
                    </EmptyState>
                )}
            </div>
        </div>
    );
}

function OperationCardContent(props: { children?: React.ReactNode; item: OperationItem }) {
    const { children, item } = props;
    const { method, path, spec } = item;
    return (
        <div className="flex items-center justify-between gap-4 p-2">
            <div>
                <div className="break-all leading-6">
                    <span
                        className={`openapi-method uppercase openapi-method-${method.toLowerCase()}`}
                    >
                        {method}
                    </span>
                    <span className="openapi-path-title pointer-events-none overflow-visible whitespace-normal">
                        {path}
                    </span>
                </div>
                {spec.operationId && (
                    <div className="mt-1 break-all font-mono text-muted text-ui-small">
                        {spec.operationId}
                    </div>
                )}
                {spec.summary && <div className="mt-1 break-all text-muted">{spec.summary}</div>}
            </div>
            {children}
        </div>
    );
}

function SchemaCardContent(props: { children?: React.ReactNode; item: SchemaItem }) {
    const { children, item } = props;
    const { value } = item;
    return (
        <div className="flex items-center justify-between gap-4 p-2">
            <div>
                <div className="break-all leading-6">
                    <span className="openapi-schema-title overflow-visible whitespace-normal">
                        {value}
                    </span>
                </div>
            </div>
            {children}
        </div>
    );
}

function WebhookCardContent(props: { children?: React.ReactNode; item: WebhookItem }) {
    const { children, item } = props;
    const { method, name } = item;
    return (
        <div className="flex items-center justify-between gap-4 p-2">
            <div>
                <div className="break-all leading-6">
                    <span
                        className={`openapi-method uppercase openapi-method-${method.toLowerCase()}`}
                    >
                        {method}
                    </span>
                    <span className="openapi-path-title pointer-events-none overflow-visible whitespace-normal">
                        {name}
                    </span>
                </div>
            </div>
            {children}
        </div>
    );
}

function CardContent(props: {
    item: OperationItem | SchemaItem | WebhookItem;
    children: React.ReactNode;
}) {
    const { item, children } = props;

    const isOperation = (item: OperationItem | SchemaItem | WebhookItem): item is OperationItem => {
        return (
            'method' in item &&
            'path' in item &&
            typeof item.method === 'string' &&
            typeof item.path === 'string'
        );
    };

    const isWebhook = (item: OperationItem | SchemaItem | WebhookItem): item is WebhookItem => {
        return (
            'method' in item &&
            'name' in item &&
            typeof item.method === 'string' &&
            typeof item.name === 'string'
        );
    };

    return isOperation(item) ? (
        <OperationCardContent item={item}>{children}</OperationCardContent>
    ) : isWebhook(item) ? (
        <WebhookCardContent item={item}>{children}</WebhookCardContent>
    ) : (
        <SchemaCardContent item={item}>{children}</SchemaCardContent>
    );
}

const SEPARATOR = '$$//$$';

function formatOperation(operation: { method: string; path: string }) {
    return `${operation.method}${SEPARATOR}${operation.path}`;
}
